// Client Book backfill upload responses (SEP-352 Stage 6, ADR-0025 D8).
//
// The three re-runnable admin ingests (contacts, mapping, KYC) return the same
// envelope: per-run counts plus the exception + conflict lists the IM/EA review
// loop reads (answer #3). Exceptions/conflicts are per-run DATA, never new
// Object Types. camelCase throughout (web↔server wire).
//
// These endpoints are multipart, so they sit OUTSIDE ts-rest (like the other
// admin uploads); this schema is the shared response contract the web summary
// renders against.

import { z } from 'zod';

/** One row the ingest could not place — reported, never silently dropped. */
export const backfillExceptionSchema = z.object({
    /** A short machine reason, e.g. 'unresolved_ref', 'name_collision',
     *  'missing_sur_number', 'invalid_sur_number', 'duplicate_sur_in_file',
     *  'sur_conflict', 'no_owner'. */
    reason: z.string(),
    /** Human-readable detail for the review loop. */
    detail: z.string(),
    /** The source row's identifying reference, when known (SUR/CRM id/ref). */
    ref: z.string().nullable().optional(),
});
export type BackfillException = z.infer<typeof backfillExceptionSchema>;

/** One descriptive field the file would have changed but a human had frozen
 *  (per-field provenance, ADR-0025 D8a) — applied nowhere, reported here. */
export const backfillConflictSchema = z.object({
    /** The target row (Contact/SUR) id. */
    rowId: z.string(),
    /** The camelCase descriptive field name that was protected. */
    field: z.string(),
    /** What the file wanted to write (stringified for display). */
    fileValue: z.string().nullable(),
    /** What was kept (the human-edited value, stringified). */
    keptValue: z.string().nullable(),
});
export type BackfillConflict = z.infer<typeof backfillConflictSchema>;

/** The shared per-run report every backfill ingest returns. */
export const backfillReportSchema = z.object({
    ok: z.literal(true),
    parsed: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    exceptions: z.array(backfillExceptionSchema),
    conflicts: z.array(backfillConflictSchema),
});
export type BackfillReport = z.infer<typeof backfillReportSchema>;
